import React from 'react';
import * as styles from './SearchOptionsModal.module.scss';
import CallOut from '../components/CallOut';
import Modal, { ModalTitle } from '../components/Modal';
import Pill from '../components/Pill';
import Link from '../utils/router/Link';

interface SearchOptionsModalItemsProps {
  items: { title: string; description: string; example: string }[];
}

const SearchOptionsModalItems = ({ items }: SearchOptionsModalItemsProps) => (
  <>
    {items.map(({ title, description, example }) => (
      <div key={title} className={styles.item}>
        <div>
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>{description}</div>
        </div>
        <div className={styles.example}>
          <Pill size="small" type="facet" noWrap>
            {example}
          </Pill>
        </div>
      </div>
    ))}
  </>
);

interface SearchOptionsModalProps {
  close(): void;
}

const SearchOptionsModal = ({ close }: SearchOptionsModalProps) => (
  <Modal width={810}>
    <ModalTitle
      close={close}
      subtitle={
        <>
          Use search filters and syntax to refine your results. When used, Slab
          will only find results that fulfill <strong>all of</strong> your
          modifiers.
        </>
      }
    >
      Advanced search options
    </ModalTitle>
    <div className={styles.container}>
      <div className={styles.facets}>
        <div className={styles.header}>Filters</div>
        <SearchOptionsModalItems
          items={[
            {
              title: 'is:',
              description:
                'Draft, published, archived, public, verified, expired',
              example: 'is: Archived',
            },
            {
              title: 'in:',
              description: 'Posts or comments in this topic or its subtopics',
              example: 'in: Marketing',
            },
            {
              title: 'mention:',
              description:
                'Entities such as posts or comments that mention this entity',
              example: 'mention: Design',
            },
            {
              title: 'by:',
              description: 'Posts or comments authored by this user',
              example: 'by: Jon Snowe',
            },
            {
              title: 'owner:',
              description: 'Posts owned by this user',
              example: 'owner: Alix Xu',
            },
          ]}
        />
        <div className={styles.tip}>
          <CallOut>
            <strong>Tip:</strong> Try “me” for any user filter, ex “by:me”
          </CallOut>
        </div>
      </div>
      <div className={styles.date}>
        <div className={styles.header}>Date filters</div>
        <SearchOptionsModalItems
          items={[
            {
              title: 'edited>',
              description: 'Posts edited before or after date',
              example: 'edited> Aug 20',
            },
            {
              title: 'published<',
              description: 'Posts published before or after date',
              example: 'published< Sep 12',
            },
            {
              title: 'verified<',
              description: 'Posts verified before or after date',
              example: 'verified< Jul 2, 2021',
            },
            {
              title: 'archived>',
              description: 'Posts archived before or after date',
              example: 'archived> Jul 2',
            },
          ]}
        />
      </div>
      <div className={styles.syntax}>
        <div className={styles.header}>Syntax</div>
        <SearchOptionsModalItems
          items={[
            {
              title: 'Exact match',
              description: 'Results must match the quoted query exactly',
              example: '"TPS report"',
            },
            {
              title: 'Exclude',
              description: 'Results must not contain the negated word',
              example: 'TPS report -memo',
            },
          ]}
        />
      </div>
    </div>
    <div className={styles.footer}>
      <Link to="https://go.slab.com/advanced-searching" newTab>
        View full guide to all search options
      </Link>
    </div>
  </Modal>
);

export default SearchOptionsModal;
